import { createFileRoute } from '@tanstack/react-router';
import { LogsConsole } from '@/features/admin/LogsConsole';

interface LogsSearch {
  tab?: 'stats';
  /** Granularità della timeline. `hour` è il default e non compare nell'URL. */
  bucket?: 'minute' | 'day';
  /**
   * `traceId` della catena aperta. Sta nell'URL — a differenza della riga selezionata —
   * perché è l'unico dei due che il server sa risolvere da solo: è un link che si può
   * mandare a un collega e che al ricaricamento funziona.
   */
  trace?: string;
  range?: '1h' | '24h' | '7d' | '30d' | 'custom';
  from?: string;
  to?: string;
  logType?: string;
  method?: string;
  statusCode?: number;
  statusClass?: string;
  onlyErrors?: boolean;
  minDurationMs?: number;
  traceId?: string;
  userEmail?: string;
  tenantId?: string;
  route?: string;
  page?: number;
  size?: number;
}

const text = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
const toNumber = (v: unknown): number | undefined =>
  (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/**
 * I filtri vivono **nell'URL**, non in uno stato locale.
 *
 * Su una pagina di diagnostica il gesto principale è incollare a un collega *l'elenco che sto
 * guardando*: con lo stato in memoria non esiste niente da mandare. Vale anche per il passaggio
 * dal grafico all'elenco, che è una navigazione a tutti gli effetti — e per il tasto Indietro,
 * che gratis riporta al grafico solo se lo stato è nell'indirizzo.
 *
 * Ogni parametro al valore predefinito viene **omesso**: un URL corto si legge, e la chiave di
 * cache resta stabile fra due modi di dire la stessa cosa.
 */
export const Route = createFileRoute('/_app/admin/logs')({
  validateSearch: (s: Record<string, unknown>): LogsSearch => ({
    tab: s.tab === 'stats' ? 'stats' : undefined,
    bucket: s.bucket === 'minute' || s.bucket === 'day' ? s.bucket : undefined,
    trace: text(s.trace),
    range: ['1h', '24h', '7d', '30d', 'custom'].includes(s.range as string) && s.range !== '24h'
      ? (s.range as LogsSearch['range'])
      : undefined,
    from: text(s.from),
    to: text(s.to),
    logType: text(s.logType),
    method: text(s.method),
    statusCode: toNumber(s.statusCode),
    statusClass: text(s.statusClass),
    onlyErrors: s.onlyErrors === true ? true : undefined,
    minDurationMs: toNumber(s.minDurationMs),
    traceId: text(s.traceId),
    userEmail: text(s.userEmail),
    tenantId: text(s.tenantId),
    route: text(s.route),
    page: toNumber(s.page) && (s.page as number) > 1 ? Math.floor(s.page as number) : undefined,
    size: toNumber(s.size) && (s.size as number) !== 50 ? Math.floor(s.size as number) : undefined,
  }),
  component: LogsConsole,
});
